import { useEffect, useRef, useState } from 'react';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import type { CharModeOptions } from '@/kit/inputCharMode';
import { Select, SelectOption } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import {
  formatDisplayDate,
  parseTypedDate,
  toNumberOrNull,
  typedDateValidator,
  type ReportOptionItem,
  type ShareCapitalDialogData,
  type ShareCapitalRow,
} from '@/core/models/report-options.models';
import { notifyError } from '@/core/notify/notify';
import { reportOptionsService } from '@/core/services/reportOptions';
import { validateForm } from './common';

export const WHOLE_NUMBER = /^\s*\d[\d,]*\s*$/;
export const AMOUNT = /^\s*\d[\d,]*(\.\d{1,4})?\s*$/;

interface Form {
  shareType: string | null;
  issueDate: string;
  noOfShares: string;
  buyBackDate: string;
  buyBackShares: string;
  shareValue: string;
  shareClass: string;
  shareClassType: string | null;
  noOfShareAuthorised: string;
  noOfSharesAlloted: string;
  sharesPartlyPaid: boolean;
  /** APT338 follow-up 2: the amount paid on each share (a partly paid class only). */
  paidUpPerShare: string;
}

type TextName = Exclude<keyof Form, 'shareType' | 'shareClassType' | 'sharesPartlyPaid'>;

const text = (value: number | null | undefined): string => (value === null || value === undefined ? '' : `${value}`);

const empty = (value: unknown) => value === null || value === undefined || (value as string).length === 0;
/** BUG-030: a mandatory field holding only spaces is blank, as in the old application (it used to be sent as null). */
const blank = (value: string) => empty(value) || value.trim().length === 0;
const pattern = (regex: RegExp) => (value: string) => empty(value) || regex.test(value);

/** Angular's validators, control by control: `required`, `pattern`, `maxLength` and `typedDateValidator`. */
const VALIDATORS: Record<TextName, ((value: string) => boolean)[]> = {
  issueDate: [(v) => !blank(v), (v) => typedDateValidator({ value: v }) === null],
  noOfShares: [(v) => !blank(v), pattern(WHOLE_NUMBER)],
  buyBackDate: [(v) => typedDateValidator({ value: v }) === null],
  buyBackShares: [pattern(WHOLE_NUMBER)],
  shareValue: [pattern(AMOUNT)],
  shareClass: [(v) => empty(v) || v.length <= 100],
  noOfShareAuthorised: [pattern(WHOLE_NUMBER)],
  noOfSharesAlloted: [pattern(WHOLE_NUMBER)],
  paidUpPerShare: [pattern(AMOUNT)],
};

const isControlValid = (name: TextName, value: string) => VALIDATORS[name].every((rule) => rule(value));

/** The offered options, plus the share's own option when it is no longer offered. */
const withCurrent = (items: ReportOptionItem[], code: string | null | undefined, name: string | null | undefined) =>
  !code || items.some((item) => item.code === code) ? items : [...items, { code, name: name || code }];

/** BUG-030: the old application's messages, shown under the field. */
export const REQUIRED_MESSAGE = 'This field is required.';
export const NUMBER_MESSAGE = 'Please enter a valid number.';
export const DATE_MESSAGE = 'Please enter a valid date.';

const REQUIRED: readonly TextName[] = ['issueDate', 'noOfShares'];
const DATES: readonly TextName[] = ['issueDate', 'buyBackDate'];

/** Why a control is invalid, or null when it is valid. */
const controlError = (name: TextName, value: string): string | null => {
  if (isControlValid(name, value)) return null;
  if (REQUIRED.includes(name) && blank(value)) return REQUIRED_MESSAGE;
  if (DATES.includes(name)) return DATE_MESSAGE;
  return name === 'shareClass' ? 'At most 100 characters.' : NUMBER_MESSAGE;
};

/** BUG-030: only digits (with the thousands commas and spaces the patterns accept) can be typed into a number box. */
const WHOLE_NUMBER_INPUT: CharModeOptions = { charMode: 'number', extraChars: ', ' };
const AMOUNT_INPUT: CharModeOptions = { charMode: 'number', extraChars: ', .' };

const initialForm = (share: ShareCapitalRow | undefined): Form =>
  share
    ? {
        shareType: share.shareType ?? '',
        issueDate: formatDisplayDate(share.issueDate),
        noOfShares: text(share.noOfShares),
        buyBackDate: formatDisplayDate(share.buyBackDate),
        buyBackShares: text(share.buyBackShares),
        shareValue: text(share.shareValue),
        shareClass: share.shareClass ?? '',
        shareClassType: share.shareClassType ?? '',
        noOfShareAuthorised: text(share.authorisedShares),
        noOfSharesAlloted: text(share.allottedShares),
        sharesPartlyPaid: share.isPartlyPaid,
        paidUpPerShare: text(share.paidUpPerShare),
      }
    : {
        shareType: '',
        issueDate: '',
        noOfShares: '',
        buyBackDate: '',
        buyBackShares: '',
        shareValue: '',
        shareClass: '',
        shareClassType: '',
        noOfShareAuthorised: '',
        noOfSharesAlloted: '',
        sharesPartlyPaid: false,
        paidUpPerShare: '',
      };

/** Angular AddshareCapitalDialogComponent: add or edit one share capital line. */
export function AddshareCapitalDialog({
  data,
  dialogRef,
}: DialogComponentProps<ShareCapitalDialogData, ShareCapitalRow>) {
  const [form, setForm] = useState<Form>(() => initialForm(data?.share));
  const [shareTypes, setShareTypes] = useState<ReportOptionItem[]>([]);
  const [shareClasses, setShareClasses] = useState<ReportOptionItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  /** Controls the user has left, or all of them after a failed save (Angular markAllAsTouched). */
  const [touched, setTouched] = useState<ReadonlySet<TextName>>(new Set());
  const alive = useRef(true);
  /** The share the dialog was opened for (BUG-030: it keeps a retired option). */
  const openedShare = useRef(data?.share);

  useEffect(() => {
    alive.current = true;
    reportOptionsService.getOptions().then(
      (options) => {
        if (!alive.current) return;
        // BUG-030: a share keeps an option that is no longer offered (the lists changed to the old application's), so
        // it still shows - and saves unchanged - when the share is edited.
        const share = openedShare.current;
        setShareTypes(withCurrent(options.shareTypes, share?.shareType, share?.shareTypeName));
        setShareClasses(withCurrent(options.shareClasses, share?.shareClassType, share?.shareClassTypeName));
      },
      (error) => alive.current && notifyError(error),
    );
    return () => {
      alive.current = false;
    };
  }, []);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm((current) => ({ ...current, [key]: value }));

  const formValid = (Object.keys(VALIDATORS) as TextName[]).every((name) => isControlValid(name, form[name]));

  const onSave = () => {
    if (!formValid) {
      setTouched(new Set(Object.keys(VALIDATORS) as TextName[]));
      // BUG-030: the mandatory-fields message only when a mandatory field is blank; otherwise what is wrong.
      if (REQUIRED.some((name) => blank(form[name]))) {
        validateForm(false);
      } else {
        const names = Object.keys(VALIDATORS) as TextName[];
        notifyError(
          names.map((name) => controlError(name, form[name])).find((error) => error !== null) ?? NUMBER_MESSAGE,
        );
      }
      return;
    }
    if (!data?.clientId || isSaving) return;
    // APT338 follow-up 2: the amount paid on each share cannot be more than the share value (the API checks it too).
    const paidUp = form.sharesPartlyPaid ? toNumberOrNull(form.paidUpPerShare) : null;
    const shareValue = toNumberOrNull(form.shareValue);
    if (paidUp !== null && shareValue !== null && paidUp > shareValue) {
      notifyError('The amount paid on each share cannot be more than the share value.');
      return;
    }
    setIsSaving(true);
    reportOptionsService
      .saveShareCapital({
        clientId: data.clientId,
        id: data.share?.id ?? null,
        shareType: form.shareType || null,
        issueDate: parseTypedDate(form.issueDate),
        noOfShares: toNumberOrNull(form.noOfShares),
        buyBackDate: parseTypedDate(form.buyBackDate),
        buyBackShares: toNumberOrNull(form.buyBackShares),
        shareValue: toNumberOrNull(form.shareValue),
        shareClass: `${form.shareClass ?? ''}`.trim() || null,
        shareClassType: form.shareClassType || null,
        authorisedShares: toNumberOrNull(form.noOfShareAuthorised),
        allottedShares: toNumberOrNull(form.noOfSharesAlloted),
        isPartlyPaid: !!form.sharesPartlyPaid,
        paidUpPerShare: paidUp,
      })
      .then(
        (saved) => dialogRef.close(saved),
        (error) => alive.current && notifyError(error),
      )
      .finally(() => alive.current && setIsSaving(false));
  };

  const input = (name: TextName, extra?: { placeholder?: string; maxLength?: number } & CharModeOptions) => {
    const error = touched.has(name) ? controlError(name, form[name]) : null;
    return (
      <>
        <InputField
          fieldClassName="w-200"
          name={name}
          value={form[name]}
          invalid={error !== null}
          onChange={(event) => set(name, event.target.value)}
          onBlur={() => setTouched((current) => new Set(current).add(name))}
          {...extra}
        />
        {error && <div className="text-red font-size-12">{error}</div>}
      </>
    );
  };

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>{data?.share ? 'Edit' : 'Add'} Share Capital</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="pl-5 pr-5">
            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">Share Type</p>
                <Select
                  className="w-200"
                  value={form.shareType}
                  onChange={(value) => set('shareType', value as string | null)}
                >
                  <SelectOption value="">Share Type</SelectOption>
                  {shareTypes.map((item) => (
                    <SelectOption key={item.code} value={item.code}>
                      {item.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>
              <div className="form-field">
                <p className="fw-bold">
                  Issue Date <span className="text-red">*</span>
                </p>
                {input('issueDate', { placeholder: 'DD/MM/YYYY' })}
              </div>
              <div className="form-field">
                <p className="fw-bold">
                  No of Shares <span className="text-red">*</span>
                </p>
                {input('noOfShares', WHOLE_NUMBER_INPUT)}
              </div>
            </div>
            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">Buy Back Date</p>
                {input('buyBackDate', { placeholder: 'DD/MM/YYYY' })}
              </div>
              <div className="form-field">
                <p className="fw-bold">Buy Back Shares</p>
                {input('buyBackShares', WHOLE_NUMBER_INPUT)}
              </div>
              <div className="form-field">
                <p className="fw-bold">Share Value of Each Share</p>
                {input('shareValue', AMOUNT_INPUT)}
              </div>
            </div>
            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">Share Class</p>
                {input('shareClass', { maxLength: 100, charMode: 'alphanumeric' })}
              </div>
              <div className="form-field">
                <p className="fw-bold">&nbsp;</p>
                <Select
                  className="w-200"
                  value={form.shareClassType}
                  onChange={(value) => set('shareClassType', value as string | null)}
                >
                  <SelectOption value="">Share Class</SelectOption>
                  {shareClasses.map((item) => (
                    <SelectOption key={item.code} value={item.code}>
                      {item.name}
                    </SelectOption>
                  ))}
                </Select>
              </div>
              <div className="form-field">
                <p className="fw-bold">No. of Share Authorised</p>
                {input('noOfShareAuthorised', WHOLE_NUMBER_INPUT)}
              </div>
            </div>
            <div className="d-flex gap-20">
              <div className="form-field">
                <p className="fw-bold">No. of Shares Alloted</p>
                {input('noOfSharesAlloted', WHOLE_NUMBER_INPUT)}
              </div>
            </div>
            <div>
              <Checkbox
                checked={form.sharesPartlyPaid}
                onChange={(event) => set('sharesPartlyPaid', event.target.checked)}
              >
                Shares are partly paid up
              </Checkbox>
            </div>
            {/* APT338 follow-up 2 (the client, 28 Oct 2026 21:35): the Working Papers schedule keeps the full nominal value and shows the amount paid up and unpaid. */}
            {form.sharesPartlyPaid && (
              <div className="form-field">
                <p className="fw-bold">Amount Paid on Each Share</p>
                {input('paidUpPerShare', AMOUNT_INPUT)}
                <div className="text-gray font-size-12">
                  The rest of the share value is unpaid. The Share capital working paper keeps the full nominal value and shows the paid up and unpaid amounts.
                </div>
              </div>
            )}
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
